<script>
    import { mapGetters, mapActions } from 'vuex';
    import { useAlert } from 'dashboard/composables';

    export default {
      name: 'Knowledge',
      data() {
        return {
          isLoading: true,
          visible: null,
          isSelectingActive: false, 
          selectedKnowledgeList: [], 
          isDeleting: false,
          isDeleteLoading: false,
          deleteTarget: null,
          isRerouting: false,
          isTransitioning: false, // For animations
          searchQuery: '', // For search functionality
          sortOption: 'dateAsc', // Default sort by date ascending
          sortOptions: [
            { value: 'nameAsc', label: this.$t('KNOWLEDGE.SORT_NAME_ASC') },
            { value: 'nameDesc', label: this.$t('KNOWLEDGE.SORT_NAME_DESC') },
            { value: 'dateAsc', label: this.$t('KNOWLEDGE.SORT_DATE_ASC') },
            { value: 'dateDesc', label: this.$t('KNOWLEDGE.SORT_DATE_DESC') },
            { value: 'wordsAsc', label: this.$t('KNOWLEDGE.SORT_WORDS_ASC') },
            { value: 'wordsDesc', label: this.$t('KNOWLEDGE.SORT_WORDS_DESC') }
          ],
          engineFilter: 'all', // 'all' | 'lightrag' | 'dify'
          engineFilterOptions: [
            { value: 'all', label: this.$t('KNOWLEDGE.ENGINE_FILTER_ALL') },
            { value: 'lightrag', label: this.$t('KNOWLEDGE.ENGINE_LIGHTRAG') },
            { value: 'dify', label: this.$t('KNOWLEDGE.ENGINE_DIFY') }
          ],
        };
      },
      computed: {
        ...mapGetters('stripe', ['getKnowledgeLimit', 'getInboxLimit']),
        ...mapGetters('knowledge', ['getKnowledgeList', 'getActiveKnowledgeList']),
        ...mapGetters('inboxes', ['getInboxes', 'getActiveInboxesIds']),
        activeKnowledgeList(){
          return this.getActiveKnowledgeList;
        },
        isOnboarding() {
          return this.$route.query.onboarding === 'true';
        },
        onboardingGlowStyle() {
          return {
            animation: 'onboardingGlow 2s ease-in-out infinite, pulse-scale 2s ease-in-out infinite',
            boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
          };
        },
        datasetLimit(){
          return this.getKnowledgeLimit;
        },
        knowledgeList(){
          return this.getKnowledgeList;
        },
        goToInbox(){
          return (this.getInboxes.length > this.getInboxLimit && this.getActiveInboxesIds === 0);
        },
        inboxList(){
          return this.getInboxes;
        },
        filteredList() {
          const isLargeDataset = this.knowledgeList.length > this.datasetLimit;
          const query = this.searchQuery.toLowerCase().trim();

          // First, map active status to all knowledge bases
          let list = this.knowledgeList.map((knowledge) => {
            return {
              ...knowledge,
              active: isLargeDataset 
                ? (Array.isArray(this.activeKnowledgeList) && this.activeKnowledgeList.includes(knowledge.id))
                : true,
            };
          });

          // Apply search filter if query exists
          if (query) {
            list = list.filter(knowledge => {
              return knowledge.name.toLowerCase().includes(query) ||
                (knowledge.description && knowledge.description.toLowerCase().includes(query));
            });
          }

          // Apply engine filter — a KB with no `engine` key is dify (missing
          // key means dify, not an error, same convention as the badge above)
          if (this.engineFilter !== 'all') {
            list = list.filter(knowledge => {
              const engine = knowledge.engine === 'lightrag' ? 'lightrag' : 'dify';
              return engine === this.engineFilter;
            });
          }

          // Apply sorting
          list = this.applySorting(list);
          
          // Final sorting to ensure active knowledge bases always appear first
          return list.sort((a, b) => {
            if (a.active === b.active) {
              return 0; // Keep the sort order from previous sort
            }
            return a.active ? -1 : 1; // Active first
          });
        },
        
        // Method to compute sorted knowledge bases
        searchResultCount() {
          return this.filteredList.length;
        }
      },
      methods: {
        ...mapActions('stripe', ['fetchSubscription']),
        ...mapActions('knowledge', ['fetchKnowledgeList', 'deleteKnowledge', 'fetchActiveKnowledge', 'updateActiveKnowledge']),
        
        handleAddKnowledge(){
          this.$router.push({name: 'add_knowledge'});
        },
        
        handleDeleteKnowledge(target){
          this.deleteTarget = target;
          this.animateTransition(() => {
            this.isDeleting = true;
            this.visible = null;
          });
        },
        
        async handleConfirmDelete(){
          try {
            this.isDeleteLoading = true;
            await this.deleteKnowledge({dataset_id: this.deleteTarget.id});
            useAlert(this.$t('KNOWLEDGE.DELETE_SUCCESS_MESSAGE'));
            this.cancelDelete();
            this.visible = null;
            await this.refreshPage();
          } catch(err) {
            console.error(err);
            useAlert(this.$t('KNOWLEDGE.DELETE_FAIL_MESSAGE'));
          } finally {
            this.isDeleteLoading = false;
          }
        },
        
        cancelDelete(){
          this.animateTransition(() => {
            this.isDeleting = false;
            this.deleteTarget = null;
          });
        },
        
        async handleSelectActive(){
          const payload = this.selectedKnowledgeList;
          try {
            await this.updateActiveKnowledge(payload);
            useAlert('Active knowledge bases updated');
            this.animateTransition(() => {
              this.isSelectingActive = false;
              this.selectedKnowledgeList = [];
            });
            await this.refreshPage();
          } catch(err) {
            console.error(err);
            useAlert('Failed to update active knowledge bases');
          }
        },
        
        async refreshPage(){
          try {
            this.isLoading = true;
            await this.fetchKnowledgeList();
            await this.fetchSubscription();
            
            if(this.knowledgeList.length > this.datasetLimit){
              await this.fetchActiveKnowledge();
              if(!this.activeKnowledgeList || this.activeKnowledgeList.length === 0){
                this.isSelectingActive = true;
              }
            }
          } catch (err) {
            console.error(err);
            useAlert('Failed to load knowledge bases');
          } finally {
            this.isLoading = false;
          }
        },
        
        // Close all modals
        closeAllModals() {
          this.visible = null;
          if (this.isDeleting) this.cancelDelete();
          if (this.isSelectingActive && this.activeKnowledgeList && this.activeKnowledgeList.length > 0) {
            this.animateTransition(() => {
              this.isSelectingActive = false;
            });
          }
          if (this.isRerouting && !this.goToInbox) {
            this.animateTransition(() => {
              this.isRerouting = false;
            });
          }
        },
        
        // Toggle selection for keyboard navigation
        toggleSelection(id) {
          const index = this.selectedKnowledgeList.indexOf(id);
          const isDisabled = this.selectedKnowledgeList.length >= this.datasetLimit && !this.selectedKnowledgeList.includes(id);
          
          if (!isDisabled) {
            if (index === -1) {
              this.selectedKnowledgeList.push(id);
            } else {
              this.selectedKnowledgeList.splice(index, 1);
            }
          }
        },
        
        // Animation helper
        animateTransition(callback) {
          this.isTransitioning = true;
          setTimeout(() => {
            callback();
            setTimeout(() => {
              this.isTransitioning = false;
            }, 300);
          }, 50);
        },
        
        // Programmatically navigate to knowledge base view
        navigateToKnowledgeBase(id) {
          this.$router.push(`knowledge/view/${id}`);
        },
        
        // Handle search query changes
        handleSearch(event) {
          this.searchQuery = event.target.value;
        },
        
        // Clear search query
        clearSearch() {
          this.searchQuery = '';
        },

        // Clear both search query and engine filter
        clearFilters() {
          this.searchQuery = '';
          this.engineFilter = 'all';
        },
        
        // Handle sort option change
        handleSortChange(event) {
          this.sortOption = event.target.value;
        },

        // Handle engine filter change
        handleEngineFilterChange(event) {
          this.engineFilter = event.target.value;
        },

        // updated_at/created_at are ISO-8601 strings for both engines.
        timeOf(isoTimestamp) {
          return isoTimestamp ? new Date(isoTimestamp).getTime() || 0 : 0;
        },
        
        // Apply sorting based on selected option
        applySorting(list) {
          switch (this.sortOption) {
            case 'nameAsc':
              return [...list].sort((a, b) => a.name.localeCompare(b.name));
            case 'nameDesc':
              return [...list].sort((a, b) => b.name.localeCompare(a.name));
            case 'dateDesc':
              return [...list].sort((a, b) => this.timeOf(a.updated_at) - this.timeOf(b.updated_at));
            case 'dateAsc':
              return [...list].sort((a, b) => this.timeOf(b.updated_at) - this.timeOf(a.updated_at));
            case 'wordsAsc':
              return [...list].sort((a, b) => (a.true_word_count || 0) - (b.true_word_count || 0));
            case 'wordsDesc':
              return [...list].sort((a, b) => (b.true_word_count || 0) - (a.true_word_count || 0));
            default:
              return list;
          }
        }
      },
      
      mounted() {
        this.isRerouting = false;
        if(this.goToInbox){
          this.isRerouting = true;
        }
        
        // Add keyboard navigation listeners
        window.addEventListener('keydown', this.handleKeyDown);
        
        this.refreshPage();
      },
      
      beforeUnmount() {
        // Remove event listeners
        window.removeEventListener('keydown', this.handleKeyDown);
      },
      
      // Keyboard navigation handler
      handleKeyDown(event) {
        // Close modals on escape key
        if (event.key === 'Escape') {
          this.closeAllModals();
        }
      }
    };
</script>

<template>
    <div class="p-2 md:p-8 flex flex-1 h-full gap-3 flex-col m-0 bg-slate-25 dark:bg-slate-900 overflow-auto">
      <!-- Header Section -->
      <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h1
          class="flex items-center text-2xl text-slate-900 dark:text-slate-100 mb-4 md:mb-0"
        >
          <fluent-icon
            icon="book"
            class="hidden ml-1 mr-2 rtl:ml-2 rtl:mr-1 md:block text-woot-500"
            aria-hidden="true"
          />
          <span class="text-2xl font-medium text-slate-900 dark:text-slate-100">
            {{ $t('KNOWLEDGE.TITLE') }}
            <span 
              v-if="!isLoading" 
              class="text-sm font-normal bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded-full ml-2"
              aria-label="Knowledge base count and limit"
            >
              {{ knowledgeList.length }} / {{ datasetLimit }}
            </span>
          </span>
        </h1>
        <div class="flex gap-4 items-center flex-wrap">
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            icon="edit"
            v-if="knowledgeList.length > datasetLimit"
            @click="() => isSelectingActive = true"
            aria-label="Select active knowledge bases" 
          >{{ $t('KNOWLEDGE.SELECT_ACTIVE_BUTTON') }}</woot-button>
          <div class="relative inline-block">
            <woot-button
              variant="smooth"
              color-scheme="primary"
              icon="plus-sign"
              :disabled="knowledgeList.length >= datasetLimit"
              @click="handleAddKnowledge"
              aria-label="Create new knowledge base"
              data-testid="create_knowledge_button"
              data-guide-target="knowledge-create-button"
              :class="'transition-all duration-300 transform hover:scale-105'"
              :style="isOnboarding ? onboardingGlowStyle : null"
            >
              {{ $t('KNOWLEDGE.CREATE_KNOWLEDGE_BUTTON') }}
            </woot-button>
            <!-- Onboarding Hint Tooltip -->
            <div v-if="isOnboarding" class="absolute -bottom-10 right-0 transform translate-x-1 whitespace-nowrap z-10 pointer-events-none">
              <div class="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-3 py-2 rounded-md shadow-lg animate-bounce-subtle flex gap-1 items-center">
                Hint: Click to create your first knowledge base <fluent-icon icon="caret-up" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Description Section -->
      <div class="flex flex-col gap-3 text-slate-600 dark:text-slate-300 w-full mb-6 bg-slate-50 dark:bg-slate-800/30 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
        <p
          class="mb-0 text-base font-normal line-clamp-5 sm:line-clamp-none tracking-[-0.1px]"
        >
          {{ $t('KNOWLEDGE.DESCRIPTION') }}
        </p>
      </div>

      <!-- Search and Sorting Controls -->
      <div class="flex flex-col md:flex-row gap-4 mb-6 items-start md:items-center">
        <!-- Search Bar -->
        <div class="relative flex-grow max-w-md">
          <input
            type="text"
            class="bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg py-2 pl-4 pr-10 w-full focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all"
            :placeholder="$t('KNOWLEDGE.QUERY_PLACEHOLDER')"
            v-model="searchQuery"
            @input="handleSearch"
            aria-label="Search knowledge bases"
            data-testid="knowledge_search_input"
          />
          <div
            v-if="!searchQuery"
            class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10 pointer-events-none"
          >
            <fluent-icon icon="search" class="h-5 w-5 text-slate-400" aria-hidden="true" />
          </div>
          <button
            v-else
            type="button"
            @click="clearSearch"
            class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10 p-0 m-0 border-0 bg-transparent"
            aria-label="Clear search"
          >
            <fluent-icon icon="dismiss" class="h-5 w-5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" />
          </button>
        </div>
        
        <!-- Engine Filter -->
        <div class="relative flex-shrink-0 w-full md:w-auto">
          <select
            v-model="engineFilter"
            @change="handleEngineFilterChange"
            class="bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg py-2 px-4 w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all appearance-none cursor-pointer pr-10"
            aria-label="Filter knowledge bases by engine"
            data-testid="knowledge_engine_filter_select"
          >
            <option v-for="option in engineFilterOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Sort Options -->
        <div class="relative flex-shrink-0 w-full md:w-auto">
          <select
            v-model="sortOption"
            @change="handleSortChange"
            class="bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg py-2 px-4 w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all appearance-none cursor-pointer pr-10"
            aria-label="Sort knowledge bases"
            data-testid="knowledge_sort_select"
          >
            <option v-for="option in sortOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Search/Filter Results Count -->
        <div v-if="searchQuery || engineFilter !== 'all'" class="text-sm text-slate-600 dark:text-slate-300 flex-shrink-0">
          {{ searchResultCount }} {{ searchResultCount > 1 ? $t('KNOWLEDGE.SEARCH_RESULTS_COUNT_PLURAL') : $t('KNOWLEDGE.SEARCH_RESULTS_COUNT') }}
        </div>
      </div>


      <!-- Skeleton Loader for Knowledge Base Cards -->
      <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div 
          v-for="i in 6" 
          :key="i" 
          class="bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden shadow-sm animate-pulse"
          style="height: 180px;"
          aria-label="Loading knowledge bases"
        >
          <div class="h-full flex flex-col p-4">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700"></div>
              <div class="ml-3 flex-1">
                <div class="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-2"></div>
                <div class="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
              </div>
            </div>
            <div class="flex-1">
              <div class="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full mb-2"></div>
              <div class="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full mb-2"></div>
              <div class="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Knowledge Base Cards -->
      <div 
        v-if="!isLoading" 
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        aria-label="Knowledge base list"
      >
        <router-link
          v-for="(base, index) in filteredList"
          :key="index"
          :to="base.active ? `knowledge/view/${base.id}` : ''"
          class="relative bg-white dark:bg-slate-800 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 rounded-lg border dark:border-slate-700 border-slate-200 flex flex-col"
          :class="{
            'opacity-70 cursor-not-allowed':!base.active,
            'cursor-pointer':base.active,
          }"
          :aria-disabled="!base.active"
          tabindex="0"
          data-testid="knowledge_card"
          :data-id="base.id"
          @keyup.enter="base.active ? $router.push(`knowledge/view/${base.id}`) : null"
        >
          <!-- Active/Inactive Status Badge -->
          <div 
            v-if="knowledgeList.length > datasetLimit" 
            class="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium"
            :class="base.active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'"
            :aria-label="base.active ? 'Active knowledge base' : 'Inactive knowledge base'"
          >
            {{ base.active ? 'Active' : 'Inactive' }}
          </div>
              
          <!-- Dropdown Menu -->
          <div 
            v-show="visible === index"
            class="absolute top-12 right-3 py-1 bg-white dark:bg-slate-700 rounded-md shadow-lg border border-slate-200 dark:border-slate-600 z-50 w-48 transition-all transform origin-top-right cursor-pointer"
            :class="{'scale-100 opacity-100': visible === index, 'scale-95 opacity-0': visible !== index}"
            aria-label="Knowledge base actions"
          >
            <button
              class="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center"
              @click.prevent="handleDeleteKnowledge(base)"
              aria-label="Delete knowledge base"
              data-testid="knowledge_delete_button"
            >
              <fluent-icon icon="delete" class="mr-2 h-4 w-4" />
              {{ $t('KNOWLEDGE.DELETE_BUTTON') }}
            </button>
          </div>
              
          <!-- More Actions Button -->
          <button
            class="absolute top-2 right-2 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            @click.prevent="visible = visible === index ? null : index"
            aria-label="More actions"
            aria-expanded="visible === index"
            aria-haspopup="true"
            data-testid="knowledge_more_actions_button"
          >
            <fluent-icon icon="more-vertical" class="h-5 w-5" />
          </button>
              
          <!-- Card Content -->
          <div class="flex-1 p-4 flex flex-col">
            <!-- Header -->
            <div class="mb-3">
              <div class="flex items-start">
                <div class="flex-shrink-0 bg-woot-50 dark:bg-woot-900/30 p-2 rounded-lg">
                  <fluent-icon
                    icon="document"
                    class="h-9 w-9 text-woot-500 dark:text-woot-400"
                    aria-hidden="true"
                  />
                </div>
                <div class="ml-3 flex-1 min-w-0">
                  <h4 class="text-base font-medium text-slate-900 dark:text-slate-100 truncate" :title="base.name">
                    {{ base.name }}
                  </h4>
                  
                  <!-- Progress Indicators -->
                  <div class="mt-1 flex flex-wrap gap-2">
                    <div
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                      :class="
                        base.engine === 'lightrag'
                          ? 'bg-woot-100 text-woot-800 dark:bg-woot-900/30 dark:text-woot-200'
                          : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                      "
                      data-testid="knowledge_card_engine_badge"
                    >
                      {{
                        base.engine === 'lightrag'
                          ? $t('KNOWLEDGE.ENGINE_LIGHTRAG')
                          : $t('KNOWLEDGE.ENGINE_DIFY')
                      }}
                    </div>
                    <div v-if="base.document_count" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-woot-100 text-woot-800 dark:bg-woot-900/30 dark:text-woot-200">
                      <span class="sr-only">{{ $t('KNOWLEDGE.DOCUMENT_COUNT_TITLE') }}:</span>
                      {{ base.document_count }} {{ base.document_count > 1 ? $t('KNOWLEDGE.DOCUMENT_COUNT_PLURAL') : $t('KNOWLEDGE.DOCUMENT_COUNT') }}
                    </div>
                    <div v-if="base.true_word_count" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200">
                      <span class="sr-only">{{ $t('KNOWLEDGE.WORD_COUNT_TITLE') }}:</span>
                      {{ base.true_word_count }} {{ base.true_word_count > 1 ? $t('KNOWLEDGE.WORD_COUNT_PLURAL') : $t('KNOWLEDGE.WORD_COUNT') }}
                    </div>
                    <div v-if="base.app_count" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                      <span class="sr-only">{{ $t('KNOWLEDGE.APP_COUNT_TITLE') }}:</span>
                      {{ $t('KNOWLEDGE.APP_COUNT', { count: base.app_count }) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
                  
            <!-- Description -->
            <p class="text-sm text-slate-600 dark:text-slate-300 line-clamp-3 flex-1">
              {{ base.description ? base.description : $t('KNOWLEDGE.FALLBACK_KNOWLEDGE_DESCRIPTION', { name: base.name }) }}
            </p>
                  
            <!-- View Button (only appears on hover) -->
            <div 
              v-if="base.active" 
              class="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700 flex justify-end opacity-0 group-hover:opacity-100 transition-opacity"
              aria-hidden="true"
            >
              <span class="text-xs font-medium text-woot-500 flex items-center">
                {{ $t('KNOWLEDGE.VIEW_DETAILS') }}
                <fluent-icon icon="chevron-right" class="ml-1 h-3 w-3" />
              </span>
            </div>
          </div>
        </router-link>
      </div>


      <!-- No Search Results -->
      <div 
        v-if="filteredList.length === 0 && knowledgeList.length > 0 && !isLoading" 
        class="py-10 flex flex-col items-center justify-center bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 px-4 md:px-8"
        aria-label="No search results found"
      >
        <div class="mb-6 w-24 h-24 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
          <fluent-icon
            icon="search"
            class="h-12 w-12 text-slate-500 dark:text-slate-400"
            aria-hidden="true"
          />
        </div>
        <h2 class="text-xl font-medium text-slate-900 dark:text-slate-100 mb-2">{{ $t('KNOWLEDGE.NO_RESULT_TITLE') }}</h2>
        <p class="text-slate-600 dark:text-slate-300 text-center mb-6 max-w-md">
          {{ $t('KNOWLEDGE.NO_RESULT_DESCRIPTION') }}
        </p>
        <woot-button
          variant="smooth"
          color-scheme="secondary"
          @click="clearFilters"
          aria-label="Clear search and filters"
        >
          {{ $t('KNOWLEDGE.CLEAR_SEARCH_BUTTON') }}
        </woot-button>
      </div>
      <!-- Empty State -->
      <div 
        v-if="knowledgeList.length == 0 && !isLoading" 
        class="py-10 flex flex-col items-center justify-center bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 px-4 md:px-8"
        aria-label="No knowledge bases found"
      >
        <div class="mb-6 w-24 h-24 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
          <fluent-icon
            icon="lightbulb"
            class="h-12 w-12 text-woot-500"
            aria-hidden="true"
          />
        </div>
        <h2 class="text-xl font-medium text-slate-900 dark:text-slate-100 mb-2">{{ $t('KNOWLEDGE.EMPTY_STATE_TITLE') }}</h2>
        <p class="text-slate-600 dark:text-slate-300 text-center mb-6 max-w-md">
          {{ $t('KNOWLEDGE.EMPTY_STATE_DESCRIPTION') }}
        </p>
        <woot-button
          variant="smooth"
          color-scheme="primary"
          icon="plus-sign"
          @click="() => handleAddKnowledge()"
          aria-label="Create first knowledge base"
          class="transition-all duration-300 transform hover:scale-105"
          data-testid="knowledge_empty_state_create_button"
          data-guide-target="knowledge-create-button"
        >{{ $t('KNOWLEDGE.EMPTY_STATE_CREATE_BUTTON') }}</woot-button>
      </div>

      <!-- Modals Background Overlay -->
      <div 
        v-if="isDeleting || isSelectingActive || isRerouting || visible !== null" 
        class="z-40 fixed inset-0"
        :class="{
          'opacity-100': isDeleting || isSelectingActive || isRerouting || visible !== null, 
          'opacity-0': !isDeleting && !isSelectingActive && !isRerouting && visible === null,
          'bg-slate-900/50 dark:bg-slate-800/80': isDeleting || isSelectingActive,
        }"
        @click="closeAllModals"
        aria-hidden="true"
      ></div>

      <!-- Delete Confirmation Modal -->
      <div 
        v-if="isDeleting" 
        class="z-50 md:min-w-[350px] fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 rounded-lg shadow-lg overflow-hidden transition-all duration-300"
        :class="{'opacity-100 scale-100': isDeleting, 'opacity-0 scale-95': !isDeleting}"
        role="dialog"
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <div class="bg-red-50 dark:bg-red-900/30 px-6 py-4 flex items-center border-b border-red-100 dark:border-red-800">
          <div class="flex-shrink-0 bg-red-100 dark:bg-red-800 p-2 rounded-full">
            <fluent-icon icon="delete" class="h-5 w-5 text-red-600 dark:text-red-300" />
          </div>
          <h2 id="delete-dialog-title" class="ml-3 text-lg font-medium text-red-800 dark:text-red-200">{{ $t('KNOWLEDGE.DELETE_DIALOG_TITLE') }}</h2>
        </div>
        <div class="px-6 py-4">
          <p id="delete-dialog-description" class="text-slate-600 dark:text-slate-300" v-html="$t('KNOWLEDGE.DELETE_CONFIRMATION', { name: deleteTarget.name || '' })">
          </p>
        </div>
        <div class="px-6 py-3 bg-slate-50 dark:bg-slate-700/30 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            @click="cancelDelete"
            aria-label="Cancel delete operation"
            :disabled="isDeleteLoading"
            data-testid="knowledge_delete_cancel_button"
          >
            {{ $t('KNOWLEDGE.CANCEL_BUTTON') }}
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="alert"
            @click="() => handleConfirmDelete()"
            aria-label="Confirm delete operation"
            :disabled="isDeleteLoading"
            data-testid="knowledge_delete_confirm_button"
          >
            {{ isDeleteLoading ? $t('KNOWLEDGE.DELETING') : $t('KNOWLEDGE.DELETE_BUTTON') }}
          </woot-button>
        </div>
      </div>

      <!-- Active Knowledge Selection Modal -->
      <div 
        v-if="isSelectingActive"
        class="z-50 w-full max-w-lg overflow-hidden fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 rounded-lg shadow-lg transition-all duration-300"
        :class="{'opacity-100 scale-100': isSelectingActive, 'opacity-0 scale-95': !isSelectingActive}"
        role="dialog"
        aria-labelledby="active-selection-title"
        aria-describedby="active-selection-description"
      >
        <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 id="active-selection-title" class="text-lg font-medium text-slate-900 dark:text-slate-100">{{ $t('KNOWLEDGE.SELECT_ACTIVE_TITLE') }}</h2>
          <p id="active-selection-description" class="mt-1 text-sm text-slate-500 dark:text-slate-400" v-html="$t('KNOWLEDGE.SELECT_ACTIVE_DESCRIPTION', { limit: datasetLimit })">
          </p>
        </div>
        <div class="max-h-[60vh] overflow-y-auto p-6">
          <div class="space-y-3">
            <label  
              v-for="base in knowledgeList"
              :key="base.id" 
              class="flex items-center p-3 rounded-md transition-colors duration-150"
              :class="{
                'bg-slate-50 hover:bg-slate-100 dark:bg-slate-700/30 dark:hover:bg-slate-700/50 cursor-pointer' : 
                  selectedKnowledgeList.length < datasetLimit || selectedKnowledgeList.includes(base.id),
                'bg-slate-50/50 dark:bg-slate-800/50 opacity-50 cursor-not-allowed':
                  selectedKnowledgeList.length >= datasetLimit && !selectedKnowledgeList.includes(base.id)
              }"
              tabindex="0"
              @keyup.space="toggleSelection(base.id)"
              role="checkbox"
              :aria-checked="selectedKnowledgeList.includes(base.id)"
              :aria-disabled="selectedKnowledgeList.length >= datasetLimit && !selectedKnowledgeList.includes(base.id)"
            >
              <div class="flex-1 min-w-0 mr-3">
                <span class="block text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{{ base.name }}</span>
                <span v-if="base.document_count" class="block text-xs text-slate-500 dark:text-slate-400">
                  {{ base.document_count }} {{ $t('KNOWLEDGE.DOCUMENT_COUNT', { count: base.document_count }) }}
                </span>
              </div>
              <input
                type="checkbox" 
                v-model="selectedKnowledgeList" 
                :value="base.id"
                class="h-5 w-5 rounded-md border-2 shadow-sm text-woot-500 focus:ring-woot-500 dark:bg-slate-700 dark:checked:bg-woot-500"
                :disabled="selectedKnowledgeList.length >= datasetLimit && !selectedKnowledgeList.includes(base.id)"
                aria-label="Select as active knowledge base"
              />
            </label>
          </div>
        </div>
        <div class="px-6 py-3 bg-slate-50 dark:bg-slate-700/30 flex justify-between border-t border-slate-200 dark:border-slate-700">
          <div class="text-sm text-slate-600 dark:text-slate-300 flex items-center">
            {{ $t('KNOWLEDGE.SELECTED') }}: {{ selectedKnowledgeList.length }}/{{ datasetLimit }}
          </div>
          <woot-button 
            variant="smooth"
            color-scheme="primary" 
            @click="handleSelectActive"
            :disabled="selectedKnowledgeList.length == 0"
            aria-label="Confirm active knowledge base selection"
          >
            {{ $t('KNOWLEDGE.CONFIRM') }}
          </woot-button>
        </div>
      </div>

      <!-- Rerouting Modal -->
      <div 
        v-if="isRerouting"
        class="z-50 w-full max-w-md fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 rounded-lg shadow-lg overflow-hidden transition-all duration-300"
        :class="{'opacity-100 scale-100': isRerouting, 'opacity-0 scale-95': !isRerouting}"
        role="dialog"
        aria-labelledby="reroute-dialog-title"
      >
        <div class="bg-amber-50 dark:bg-amber-900/30 px-6 py-4 border-b border-amber-100 dark:border-amber-800">
          <h2 id="reroute-dialog-title" class="text-lg font-medium text-amber-800 dark:text-amber-200">{{ $t('KNOWLEDGE.REROUTE_DIALOG_TITLE') }}</h2>
        </div>
        <div class="px-6 py-4">
          <p class="text-slate-600 dark:text-slate-300">
            {{ $t('KNOWLEDGE.REROUTE_DIALOG_DESCRIPTION') }}
          </p>
        </div>
        <div class="px-6 py-3 bg-slate-50 dark:bg-slate-700/30 flex justify-between gap-3 border-t border-slate-200 dark:border-slate-700">
          <woot-button 
            variant="smooth"
            color-scheme="alert"
            @click="$router.back()"
            aria-label="Cancel and go back"
          > 
            {{ $t('KNOWLEDGE.CANCEL_BUTTON') }}
          </woot-button>
          <woot-button 
            variant="smooth"
            color-scheme="primary"
            aria-label="Go to inbox settings"
          > 
            <router-link :to="{name: 'settings_inbox_list'}">{{ $t('KNOWLEDGE.GO_TO_INBOX_SETTINGS') }}</router-link>
          </woot-button>
        </div>
      </div>
    </div>
</template>
<style scoped>
/* Animations and Transitions */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}

/* Onboarding Glow Effect */
@keyframes onboardingGlow {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
}

/* Pulse Scale Animation */
@keyframes pulse-scale {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}



/* Accessibility focus styles */
a:focus-visible, 
button:focus-visible, 
input:focus-visible, 
label:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* Responsive tweaks */
@media (max-width: 640px) {
  .grid-cols-1 > * {
    margin-bottom: 1rem;
  }
}

/* Improve card hover effects */
router-link {
  transition: all 0.3s ease;
}

router-link:hover .text-woot-500 {
  transform: translateX(2px);
  transition: transform 0.2s ease;
}

/* Improved accessibility for disabled elements */
[aria-disabled="true"] {
  cursor: not-allowed;
  pointer-events: none;
}

/* Screen reader only text */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
